import { useState, useEffect, useCallback } from 'react'
import AdminOffplanForm from './AdminOffplanForm'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

export default function AdminOffplanPanel({ token }) {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingProject, setEditingProject] = useState(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const fetchProjects = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await fetch('/api/adminOffplan', { headers: { 'x-admin-token': token } })
      const data = await res.json()
      setProjects(Array.isArray(data) ? data : [])
    } catch {
      setProjects([])
    } finally {
      if (!silent) setLoading(false)
    }
  }, [token])

  useEffect(() => { fetchProjects() }, [fetchProjects])

  const handleDelete = async () => {
    if (!deleteConfirm) return
    setLoading(true)
    try {
      await fetch(`/api/adminOffplan/${deleteConfirm}`, { method: 'DELETE', headers: { 'x-admin-token': token } })
    } finally {
      setDeleteConfirm(null)
      fetchProjects()
    }
  }

  const handleEdit = (proj) => {
    setEditingProject(proj)
    setIsFormOpen(true)
  }

  const handleAddNew = () => {
    setEditingProject(null)
    setIsFormOpen(true)
  }

  const handleCloseForm = (refresh) => {
    setIsFormOpen(false)
    setEditingProject(null)
    if (refresh) fetchProjects()
  }

  const existingDevelopers = Array.from(new Set(projects.map(p => p.developer).filter(Boolean)))
  const existingAreas = Array.from(new Set(projects.map(p => p.area).filter(Boolean)))

  if (isFormOpen) {
    return <AdminOffplanForm token={token} project={editingProject} existingDevelopers={existingDevelopers} existingAreas={existingAreas} onClose={handleCloseForm} />
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
      <div style={{ width: '40px', height: '40px', border: `3px solid ${GOLD}30`, borderTopColor: GOLD, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  )

  return (
    <div style={{ padding: 'clamp(16px,3vw,28px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>Properties Portfolio</h3>
          <p style={{ fontFamily: FONT, fontSize: '13px', color: '#64748b', margin: 0 }}>{projects.length} propert{projects.length !== 1 ? 'ies' : 'y'}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={fetchProjects} style={{ padding: '9px 16px', borderRadius: '8px', border: `1.5px solid ${GOLD}40`, background: `${GOLD}08`, color: NAVY, fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            ↻ Refresh
          </button>
          <button onClick={handleAddNew} style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: NAVY, color: '#fff', fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            + Add New Properties/Project
          </button>
        </div>
      </div>

      {projects.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '80px 20px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,46,83,0.06)' }}>
          <div style={{ fontSize: '52px', marginBottom: '16px' }}>🏗️</div>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 8px' }}>No properties found</h3>
          <p style={{ fontFamily: FONT, fontSize: '14px', color: '#94a3b8', margin: 0 }}>Add your first property manually.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {projects.map(p => (
            <div key={p.id} style={{ background: '#fff', borderRadius: '14px', padding: '16px 20px', boxShadow: '0 1px 6px rgba(0,46,83,0.06)', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <div style={{ width: '68px', height: '52px', borderRadius: '10px', overflow: 'hidden', background: '#f0f4f8', flexShrink: 0 }}>
                {p.cover_image?.url ? (
                  <img src={p.cover_image.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>🏗️</div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <div style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: NAVY }}>{p.name}</div>
                  <span style={{ background: '#f1f5f9', color: '#475569', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', padding: '2px 6px', borderRadius: '4px' }}>{p.category || 'offplan'}</span>
                </div>
                <div style={{ fontFamily: FONT, fontSize: '12px', color: '#94a3b8' }}>
                  {p.developer} · {p.area}{p.emirate ? `, ${p.emirate}` : ''} · {p.status}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0, paddingRight: '12px' }}>
                <div style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 800, color: NAVY }}>{p.startingPrice ? `AED ${Number(p.startingPrice).toLocaleString()}` : 'Price on Request'}</div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button 
                  onClick={async () => {
                    const isVisible = p.isVisible === false ? true : false;
                    await fetch(`/api/adminOffplan/${p.id}/visibility`, {
                      method: 'PATCH',
                      headers: { 'x-admin-token': token, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ isVisible })
                    });
                    fetchProjects();
                  }} 
                  style={{ padding: '6px 12px', borderRadius: '6px', border: `1px solid ${p.isVisible === false ? '#d1d5db' : '#a7f3d0'}`, background: p.isVisible === false ? '#f3f4f6' : '#ecfdf5', color: p.isVisible === false ? '#6b7280' : '#059669', fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                  title="Toggle visibility on website"
                >
                  {p.isVisible === false ? 'Hidden' : 'Visible'}
                </button>
                <button onClick={() => handleEdit(p)} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #eaeef3', background: '#f8fafb', color: NAVY, fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                <button onClick={() => setDeleteConfirm(p.id)} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #fca5a5', background: 'transparent', color: '#dc2626', fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Custom Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,16,32,0.6)', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', border: `1px solid #fca5a5`, animation: 'fadeIn 0.2s ease' }}>
            <div style={{ fontSize: '36px', marginBottom: '16px', textAlign: 'center' }}>🗑️</div>
            <h3 style={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700, color: NAVY, textAlign: 'center', margin: '0 0 12px' }}>Delete Project?</h3>
            <p style={{ fontFamily: FONT, fontSize: '14px', color: '#64748b', textAlign: 'center', margin: '0 0 24px', lineHeight: 1.6 }}>Are you sure you want to permanently delete this off-plan project? This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #d1d5db', background: '#fff', color: NAVY, fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafb'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>Cancel</button>
              <button onClick={handleDelete} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#fff', fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(220,38,38,0.2)', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#b91c1c'} onMouseLeave={e => e.currentTarget.style.background = '#dc2626'}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
